import { StateCreator } from 'zustand'
import type { ProductsState } from './types'
import type { ContextState } from '../context'
import type { FiltersState } from '../filters'
import type { SearchFilter } from '../../../services'
import type { SharedState } from '../shared'
import { GALLERY_PAGE_SIZE } from '../../../constants'
import { useNbaFilters as GetNbaFilters } from '../../../utils/nbaFilters'
import { getIsFilterApplied } from '../../../utils/getIsFilterApplied'
import { getIntegerFilterInput } from '../../helpers'
import { createSearchCriteria } from './searchCriteria'

export const createProductsSlice: StateCreator<
  ProductsState &
    Partial<ContextState> &
    Partial<FiltersState> &
    Partial<SharedState>
> = (set, get) => ({
  initialOffset: 0,
  topPage: 1,
  bottomPage: 1,
  lastLoadedPage: 1,
  galleryPageSize: GALLERY_PAGE_SIZE,
  results: {
    totalCount: 0,
    products: [],
    suggestions: [],
    departments: [],
    page: 1,
    supportedSortOrders: [],
    totalClickRankDocumentCount: 0,
    isMultiBuySupported: false,
  },
  searchCriteria: {
    searchTerm: '',
    filters: {
      integerFilter: undefined,
      groupedFacets: [],
    },
  },
  resetInitialOffset: /* istanbul ignore next */ () =>
    set(() => ({ initialOffset: 0 })),
  querySearch: null,
  queryProducts: async ({
    newPage,
    isLoadMore,
    isPrevious,
    isLoadPage,
    filters,
    sortOrder,
    filteringEvent,
    allFilters,
    integerFilter,
  }) => {
    const {
      querySearch,
      results,
      toggleLoading,
      preAppliedFilters,
      selectedFilters,
      sortValue,
      searchContext,
      resetPage,
    } = get()

    const queryFilters = filters || selectedFilters

    /* istanbul ignore next */
    if (!querySearch || !searchContext) {
      return null
    }

    const { selectedSuggestion } = searchContext

    if (!isLoadMore || isLoadPage) {
      toggleLoading?.()
    }

    if (!isLoadMore && !isLoadPage) {
      resetPage()
    }

    const searchResponse = await querySearch({
      newPage,
      filters:
        selectedSuggestion?.facetKey && selectedSuggestion?.group
          ? [
              ...(queryFilters
                ? queryFilters.map(f => ({
                    group: f.parent,
                    key: f.id,
                  }))
                : /* istanbul ignore next */ ([] as SearchFilter[])),
              {
                group: selectedSuggestion.group,
                key: selectedSuggestion.facetKey,
              },
            ]
          : [
              ...(preAppliedFilters || /* istanbul ignore next */ []),
              ...(queryFilters
                ? queryFilters.map(f => ({
                    group: f.parent,
                    key: f.id,
                  }))
                : /* istanbul ignore next */ ([] as SearchFilter[])),
            ],
      sortOrder: sortOrder || sortValue,
      integerFilter,
    })

    let updatedProducts
    if (isLoadMore) {
      updatedProducts = isPrevious
        ? [...searchResponse.products, ...results.products]
        : [...results.products, ...searchResponse.products]
    } else {
      updatedProducts = searchResponse.products
    }

    const updatedResults = {
      ...searchResponse,
      products: updatedProducts,
    }

    toggleLoading?.(false)
    const departmentCounts = searchContext.departmentCounts.map(d => {
      if (d.department === searchContext.departments[0]) {
        return {
          ...d,
          count: updatedResults.totalCount,
        }
      }
      return d
    })

    const hasNbas = allFilters ? GetNbaFilters(allFilters).length > 0 : false
    const hasRudeProducts = allFilters
      ? getIsFilterApplied(allFilters, 'not-rude', false)
      : false

    const searchCriteria = createSearchCriteria({
      searchContext,
      integerFilter,
      preAppliedFilters: preAppliedFilters || /* istanbul ignore next */ [],
      selectedFilters: selectedFilters || /* istanbul ignore next */ [],
      appliedIntents:
        searchResponse.appliedIntents || /* istanbul ignore next */ [],
    })

    set(() => ({
      results: updatedResults,
      selectedFilters: queryFilters,
      filteringEvent,
      sortValue: sortOrder || sortValue,
      hasNbaFilters: hasNbas,
      hasRudeProducts,
      searchContext: {
        ...searchContext,
        departmentCounts,
      },
      searchCriteria,
    }))
  },
  loadMoreProducts: async isPrevious => {
    const {
      results,
      queryProducts,
      topPage,
      bottomPage,
      loading,
      integerFilters,
      galleryPageSize,
    } = get()

    if (loading) {
      return true
    }

    if (
      results.products.length >= results.totalCount ||
      results.totalCount <= galleryPageSize
    ) {
      return false
    }

    const newPage = isPrevious ? topPage - 1 : bottomPage + 1

    await queryProducts({
      newPage,
      isLoadMore: true,
      isPrevious,
      integerFilter: getIntegerFilterInput(integerFilters),
    })

    set(() => ({
      lastLoadedPage: isPrevious ? topPage - 1 : bottomPage + 1,
      bottomPage: isPrevious ? bottomPage : bottomPage + 1,
      topPage: isPrevious ? topPage - 1 : topPage,
    }))

    return true
  },
  loadPage: async (page, _source) => {
    const { queryProducts } = get()
    await queryProducts({
      newPage: page,
      isLoadMore: false,
      isPrevious: false,
      isLoadPage: true,
    })

    set(() => ({
      topPage: page,
      bottomPage: page,
      lastLoadedPage: page,
    }))
  },
  resetPage: () =>
    set(() => ({
      topPage: 1,
      bottomPage: 1,
      lastLoadedPage: 1,
    })),
})
